import { FC, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { BookingTab, useDetailBookingStore } from "../detail-booking.store";
import { TutorialTip } from "../tutorial-tip";
import { capitalize } from "~/utils";
import { InventoryModal } from "~/components/admin/inventories";
import { useGetNotes } from "~/services/note";
import { useGetFiles } from "~/services/file";
import { SortOrder } from "~/utils/constants";

type Props = {};

export const BookingTabs: FC<Props> = () => {
  const { activeTab, setActiveTab, booking } = useDetailBookingStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch notes to get the count
  const { data: notesData } = useGetNotes({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Fetch documents to get the count
  const { data: documentsData } = useGetFiles(
    { jobTicketId: booking?.id },
    !!booking?.id
  );

  // Sync tab with URL params on mount
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") as BookingTab;
    if (tabFromUrl && Object.values(BookingTab).includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, setActiveTab]);

  // Handle tab change and update URL
  const handleTabChange = (tab: BookingTab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", tab);
      return newParams;
    });
  };

  const notesCount = notesData?.items?.length || 0;

  // Calculate service ticket count
  const serviceCount = booking?.serviceTicket?.length || 0;

  // Calculate document count using lazy-loaded data
  const documentCount = documentsData?.items?.length || 0;

  // Calculate unique inventory count across all service tickets
  const inventoryCount = useMemo(() => {
    if (!booking?.serviceTicket) return 0;

    const uniqueInventories = new Map();
    booking.serviceTicket.forEach((ticket) => {
      (ticket.inventories ?? []).forEach((inventory) => {
        if (!uniqueInventories.has(inventory.id)) {
          uniqueInventories.set(inventory.id, inventory);
        }
      });
    });

    return uniqueInventories.size;
  }, [booking]);

  // Calculate communication count
  const communicationCount = booking?.communications?.length || 0;

  const tabDescriptions: Record<BookingTab, string> = {
    [BookingTab.OVERVIEW]: "High-level summary of the booking — client info, services, inventory, financials, and recent notes at a glance.",
    [BookingTab.SERVICES]: "Manage service tickets (collections, deliveries, on-site, warehouse, fabrication). Add staff, set dates, track completion.",
    [BookingTab.INVENTORY]: "View and manage all artwork and items across every service in this booking. Add, edit, or reassign pieces.",
    [BookingTab.DOCUMENTS]: "Upload and manage files related to this booking — condition reports, packing lists, photos, and other attachments.",
    [BookingTab.COMMUNICATION]: "Log of all client communications — emails sent, messages, and contact history for this booking.",
    [BookingTab.NOTES]: "Internal notes visible only to your team. Use these for special instructions, updates, or reminders.",
    [BookingTab.BILLING]: "Create estimates, generate invoices, track payments, and manage QuickBooks integration for this booking.",
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm mb-6">
      <div className="flex overflow-x-auto gap-8">
        {Object.values(BookingTab).map((tab) => (
          <TutorialTip key={tab} text={tabDescriptions[tab]} placement="bottom">
            <button
              className={`px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent hover:border-gray-200"
              }`}
              onClick={() => handleTabChange(tab)}
            >
              {capitalize(tab)}
              {tab === BookingTab.NOTES && (
                <span className={`ml-1 ${activeTab === tab ? "text-blue-600" : "text-gray-500"}`}>
                  ({notesCount})
                </span>
              )}
              {tab === BookingTab.SERVICES && (
                <span className={`ml-1 ${activeTab === tab ? "text-blue-600" : "text-gray-500"}`}>
                  ({serviceCount})
                </span>
              )}
              {tab === BookingTab.DOCUMENTS && (
                <span className={`ml-1 ${activeTab === tab ? "text-blue-600" : "text-gray-500"}`}>
                  ({documentCount})
                </span>
              )}
              {tab === BookingTab.INVENTORY && (
                <span className={`ml-1 ${activeTab === tab ? "text-blue-600" : "text-gray-500"}`}>
                  ({inventoryCount})
                </span>
              )}
              {tab === BookingTab.COMMUNICATION && (
                <span className={`ml-1 ${activeTab === tab ? "text-blue-600" : "text-gray-500"}`}>
                  ({communicationCount})
                </span>
              )}
            </button>
          </TutorialTip>
        ))}
      </div>
    </div>
  );
};
